import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, FolderInput, Pencil, Plus, Trash2, Workflow } from 'lucide-react';
import { useState } from 'react';

import type { EscritorioMembro } from '@/api/client';
import type { Equipe } from '@/api/equipes';
import {
  alterarRegra,
  desfazerAplicacaoAoAcervo,
  excluirRegra,
  fetchAplicacaoAoAcervo,
  fetchRegras,
  pararAplicacaoAoAcervo,
  reordenarRegras,
  type AplicacaoAoAcervo,
  type RegraDistribuicao,
} from '@/api/regrasDistribuicao';
import { descreverResponsaveis, invalidarDistribuicao } from '@/components/escritorio/ResponsaveisDialog';
import { AplicarAoAcervoDialog } from '@/components/escritorio/regras/AplicarAoAcervoDialog';
import { RegraDialog } from '@/components/escritorio/regras/RegraDialog';
import { descreverCondicao } from '@/components/escritorio/regras/rotulos';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { formatDate } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

interface RegrasAbaProps {
  escritorioId: string;
  membros: EscritorioMembro[];
  equipes: Equipe[];
}

/**
 * As regras de distribuição do escritório (docs/PLANO_REGRAS_DISTRIBUICAO.md): na ordem em que
 * são avaliadas — vale a primeira que casar. Quem distribui cria, ordena, liga e desliga.
 */
export function RegrasAba({ escritorioId, membros, equipes }: RegrasAbaProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [editando, setEditando] = useState<RegraDistribuicao | null>(null);
  const [criando, setCriando] = useState(false);
  const [excluindo, setExcluindo] = useState<RegraDistribuicao | null>(null);
  const [aplicandoAoAcervo, setAplicandoAoAcervo] = useState(false);
  const [desfazendo, setDesfazendo] = useState(false);

  const regras = useQuery({
    queryKey: queryKeys.escritorios.regras(escritorioId),
    queryFn: () => fetchRegras(escritorioId),
  });
  // O andamento é atualizado pela página (useAcompanharLotes), em qualquer aba; aqui só se lê.
  const acervo = useQuery({
    queryKey: queryKeys.escritorios.acervo(escritorioId),
    queryFn: () => fetchAplicacaoAoAcervo(escritorioId),
  });

  const atualizar = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.regras(escritorioId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.fila(escritorioId) });
  };

  const ordenar = useMutation({
    mutationFn: (ids: string[]) => reordenarRegras(escritorioId, ids),
    onSuccess: (novas) => queryClient.setQueryData(queryKeys.escritorios.regras(escritorioId), novas),
    onError: (err) => notifyMutationError(err, showError),
    onSettled: atualizar,
  });

  const ligar = useMutation({
    mutationFn: (r: RegraDistribuicao) =>
      alterarRegra(escritorioId, r.id, {
        nome: r.nome,
        ativa: !r.ativa,
        modo: r.modo,
        condicoes: r.condicoes,
        destino: r.destino,
        atualizada_em: r.updated_at,
      }),
    onSuccess: (r) => {
      // A versão nova na hora: um segundo toque não sai com o updated_at velho (409 falso).
      queryClient.setQueryData<RegraDistribuicao[]>(queryKeys.escritorios.regras(escritorioId), (lista) =>
        lista?.map((x) => (x.id === r.id ? r : x)),
      );
      showSuccess(r.ativa ? 'Regra ligada' : 'Regra desligada');
    },
    onError: (err) => notifyMutationError(err, showError),
    onSettled: atualizar,
  });

  // Parar ou desfazer muda quem cuida: tudo o que mostra responsável (lista, painel, agenda).
  const atualizarAcervo = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.acervo(escritorioId) });
    atualizar();
    invalidarDistribuicao(queryClient, escritorioId);
  };

  const parar = useMutation({
    mutationFn: () => pararAplicacaoAoAcervo(escritorioId),
    onSuccess: () => showSuccess('Aplicação ao acervo parada'),
    onError: (err) => notifyMutationError(err, showError),
    onSettled: atualizarAcervo,
  });

  const desfazer = useMutation({
    mutationFn: (loteId: string) => desfazerAplicacaoAoAcervo(escritorioId, loteId),
    onSuccess: ({ desfeitos }) => {
      showSuccess(
        desfeitos === 1
          ? '1 processo voltou a ficar sem responsável'
          : `${desfeitos.toLocaleString('pt-BR')} processos voltaram a ficar sem responsável`,
      );
      setDesfazendo(false);
    },
    onError: (err) => {
      notifyMutationError(err, showError);
      setDesfazendo(false);
    },
    onSettled: atualizarAcervo,
  });

  const excluir = useMutation({
    mutationFn: (r: RegraDistribuicao) => excluirRegra(escritorioId, r.id),
    onSuccess: () => {
      showSuccess('Regra excluída');
      setExcluindo(null);
    },
    onError: (err) => notifyMutationError(err, showError),
    onSettled: atualizar,
  });

  if (regras.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size={32} label="Carregando as regras" />
      </div>
    );
  }
  if (regras.error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{classifyError(regras.error).message}</AlertDescription>
      </Alert>
    );
  }

  const lista = regras.data ?? [];
  const lote = acervo.data ?? null;
  const emAndamento = lote?.estado === 'PENDENTE';
  // Mexer em qualquer regra durante a aplicação a interrompe (a prévia confirmada deixa de valer):
  // as regras ficam travadas até concluir ou o gestor parar.
  const travadas = emAndamento;
  const podeAplicar = lista.some((r) => r.ativa && !r.destino_invalido);
  const mover = (de: number, para: number) => {
    const ids = lista.map((r) => r.id);
    [ids[de], ids[para]] = [ids[para], ids[de]];
    ordenar.mutate(ids);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Vale a primeira regra que casar, de cima para baixo. Sem nenhuma, a sugestão é o dono do processo.
        </p>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          {podeAplicar && !emAndamento && (
            <Button variant="outline" className="h-11 sm:h-9" onClick={() => setAplicandoAoAcervo(true)}>
              <FolderInput className="mr-2 h-4 w-4" aria-hidden="true" />
              Aplicar ao acervo existente
            </Button>
          )}
          <Button className="h-11 sm:h-9" disabled={travadas} onClick={() => setCriando(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Nova regra
          </Button>
        </div>
      </div>

      {/* Sempre montada: o leitor de tela anuncia a troca (aplicando → concluída, parada). */}
      <p role="status" className="sr-only">
        {lote ? anunciar(lote) : ''}
      </p>
      {acervo.error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(acervo.error).message}</AlertDescription>
        </Alert>
      )}
      {lote && (
        <AndamentoNoAcervo
          lote={lote}
          parando={parar.isPending}
          onParar={() => parar.mutate()}
          onDesfazer={() => setDesfazendo(true)}
        />
      )}

      {lista.length === 0 ? (
        <EmptyState
          icon={<Workflow />}
          title="Nenhuma regra ainda"
          description="Diga para quem vai cada tipo de processo — por cliente, parte, assunto, classe, valor ou tribunal — e a aba Distribuir já chega com a sugestão."
        />
      ) : (
        <ol className="space-y-2">
          {lista.map((r, i) => (
            <li key={r.id}>
              <Card className={`flex flex-col gap-3 p-3 sm:flex-row sm:items-start ${r.ativa ? '' : 'opacity-60'}`}>
                <div className="flex shrink-0 gap-1 sm:flex-col">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 sm:h-8 sm:w-8"
                    disabled={i === 0 || ordenar.isPending || travadas}
                    onClick={() => mover(i, i - 1)}
                    aria-label={`Subir a regra ${r.nome}`}
                  >
                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 sm:h-8 sm:w-8"
                    disabled={i === lista.length - 1 || ordenar.isPending || travadas}
                    onClick={() => mover(i, i + 1)}
                    aria-label={`Descer a regra ${r.nome}`}
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs tabular-nums text-muted-foreground">{i + 1}.</span>
                    <p className="font-medium [overflow-wrap:anywhere]">{r.nome}</p>
                    <Badge variant={r.modo === 'APLICAR' ? 'default' : 'secondary'}>
                      {r.modo === 'APLICAR' ? 'Aplica sozinha' : 'Só sugere'}
                    </Badge>
                    {r.destino_invalido && <Badge variant="destructive">Destino não vale mais</Badge>}
                  </div>
                  <ul className="space-y-0.5 text-sm text-muted-foreground">
                    {r.condicoes.map((c) => (
                      <li key={c.campo} className="[overflow-wrap:anywhere]">
                        {descreverCondicao(c)}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm">
                    Vai para{' '}
                    <span className="font-medium">{descreverResponsaveis([r.destino], equipes, membros)}</span>
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Switch
                    checked={r.ativa}
                    disabled={ligar.isPending || ordenar.isPending || travadas}
                    onCheckedChange={() => ligar.mutate(r)}
                    aria-label={r.ativa ? `Desligar a regra ${r.nome}` : `Ligar a regra ${r.nome}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 sm:h-8 sm:w-8"
                    disabled={travadas}
                    onClick={() => setEditando(r)}
                    aria-label={`Editar a regra ${r.nome}`}
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 sm:h-8 sm:w-8"
                    disabled={travadas}
                    onClick={() => setExcluindo(r)}
                    aria-label={`Excluir a regra ${r.nome}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ol>
      )}

      <RegraDialog
        escritorioId={escritorioId}
        open={criando || !!editando}
        onOpenChange={(v) => {
          if (!v) {
            setCriando(false);
            setEditando(null);
          }
        }}
        regra={editando}
        membros={membros}
        equipes={equipes}
      />
      {/* Montado só aberto: cada abertura refaz a prévia (as regras podem ter mudado). */}
      {aplicandoAoAcervo && (
        <AplicarAoAcervoDialog
          escritorioId={escritorioId}
          open
          onClose={() => setAplicandoAoAcervo(false)}
          membros={membros}
          equipes={equipes}
        />
      )}
      <ConfirmDialog
        open={desfazendo}
        title="Desfazer a aplicação ao acervo?"
        message={`Os processos que ela distribuiu voltam a ficar sem responsável (${processos(lote?.distribuidos ?? 0)}). Os que alguém redistribuiu depois ficam como estão.`}
        confirmLabel="Desfazer"
        confirmColor="error"
        loading={desfazer.isPending}
        onConfirm={() => lote && desfazer.mutate(lote.id)}
        onCancel={() => setDesfazendo(false)}
      />
      <ConfirmDialog
        open={!!excluindo}
        title="Excluir a regra?"
        message={`A regra “${excluindo?.nome ?? ''}” deixa de sugerir e de distribuir. Se ela já distribuiu processos, desligue-a em vez de excluir (ou desfaça o que ela distribuiu na aba Distribuir).`}
        confirmLabel="Excluir"
        confirmColor="error"
        loading={excluir.isPending}
        onConfirm={() => excluindo && excluir.mutate(excluindo)}
        onCancel={() => setExcluindo(null)}
      />
    </div>
  );
}

const processos = (n: number) => (n === 1 ? '1 processo' : `${n.toLocaleString('pt-BR')} processos`);

/** A frase do leitor de tela: só o estado, não o contador (que muda a cada 5 s). */
function anunciar(lote: AplicacaoAoAcervo): string {
  if (lote.desfeito_em) return 'Aplicação ao acervo desfeita.';
  if (lote.estado === 'PENDENTE') return 'Aplicando as regras ao acervo existente.';
  if (lote.estado === 'CANCELADO') return 'Aplicação ao acervo parada.';
  return 'Regras aplicadas ao acervo existente.';
}

/** Como foi (ou vai) a última aplicação das regras ao acervo existente. */
function AndamentoNoAcervo({
  lote,
  parando,
  onParar,
  onDesfazer,
}: {
  lote: AplicacaoAoAcervo;
  parando: boolean;
  onParar: () => void;
  onDesfazer: () => void;
}) {
  const fora = lote.aguardando > 0 ? `; ${processos(lote.aguardando)} sem os dados ficaram como estavam` : '';
  if (lote.estado === 'PENDENTE') {
    return (
      <Card className="flex flex-wrap items-center gap-2 p-3 text-sm text-muted-foreground">
        <Spinner size={16} />
        <span>
          Aplicando as regras ao acervo existente… {processos(lote.distribuidos)} até agora. Para mexer nas
          regras, pare a aplicação.
        </span>
        <Button variant="ghost" size="sm" className="h-11 sm:h-7" disabled={parando} onClick={onParar}>
          Parar
        </Button>
      </Card>
    );
  }
  const quando = formatDate(lote.concluido_em ?? lote.created_at);
  const resumo =
    lote.estado === 'CANCELADO'
      ? `Aplicação ao acervo existente parada em ${quando}. ${lote.motivo ? `${lote.motivo}. ` : ''}Distribuídos: ${processos(lote.distribuidos)}${fora}.`
      : `Regras aplicadas ao acervo existente em ${quando}: ${processos(lote.distribuidos)}${fora}.`;
  return (
    <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
      <span>
        {resumo}
        {lote.desfeito_em && ` Desfeita em ${formatDate(lote.desfeito_em)}.`}
      </span>
      {!lote.desfeito_em && lote.distribuidos > 0 && (
        <Button variant="link" size="sm" className="h-11 px-0 sm:h-7" onClick={onDesfazer}>
          Desfazer
        </Button>
      )}
    </div>
  );
}
